import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useClinic } from "@/context/ClinicContext";
import ClinicMark from "@/components/ClinicMark";
import { ShieldCheck, Loader2 } from "lucide-react";

const DEMO = [
  { label: "Owner", email: "admin@relationshipos.health", password: "Admin123!" },
  { label: "Provider", email: "provider@relationshipos.health", password: "Provider123!" },
  { label: "Nurse", email: "nurse@relationshipos.health", password: "Nurse123!" },
  { label: "Front Desk", email: "frontdesk@relationshipos.health", password: "Front123!" },
];

export default function Login() {
  const { login, verifyMfa, error, user } = useAuth();
  const { clinic } = useClinic();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [code, setCode] = useState("");

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const clinicName = clinic?.name || "RelationshipOS";
  const Logo = () => <ClinicMark className="text-sm" />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res?.mfaRequired) {
      setMfaStep(true);
    } else if (res?.ok) {
      navigate("/");
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await verifyMfa(code);
    setLoading(false);
    if (res?.ok) navigate("/");
  };

  const quick = (d) => {
    setEmail(d.email);
    setPassword(d.password);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/7789616/pexels-photo-7789616.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center overflow-hidden">
            <Logo />
          </div>
          <span className="font-heading font-bold text-lg" data-testid="login-clinic-name">{clinicName}</span>
        </div>
        <div className="relative space-y-4 max-w-md">
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-tight">
            Strengthen patient relationships.
          </h1>
          <p className="text-white/70 text-lg leading-relaxed font-medium">
            Simplify recurring care. Grow your practice.
          </p>
          <p className="text-white/55 text-base leading-relaxed">
            The operating system for relationship-based healthcare — protocols, labs, renewals, and retention in one calm workspace.
          </p>
        </div>
        <div className="relative flex items-center gap-2 text-sm text-white/50">
          <ShieldCheck className="w-4 h-4 text-emerald-400" />
          Built with HIPAA-oriented security & audit logging.
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center text-primary-foreground overflow-hidden">
              <Logo />
            </div>
            <span className="font-heading font-bold text-lg">{clinicName}</span>
          </div>

          <h2 className="font-heading text-2xl font-bold tracking-tight">Staff sign in</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Access your clinic dashboard.</p>

          {mfaStep ? (
            <form onSubmit={submitMfa} className="space-y-4" data-testid="mfa-form">
              <div>
                <label className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Authentication code</label>
                <p className="text-sm text-muted-foreground mt-1 mb-2">Enter the 6-digit code from your authenticator app.</p>
                <input
                  data-testid="mfa-code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="w-full rounded-md border border-input bg-card px-3 py-2.5 text-lg tracking-[0.4em] text-center font-mono outline-none focus:ring-2 focus:ring-ring"
                  placeholder="000000"
                />
              </div>
              {error && (
                <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
              )}
              <button
                data-testid="mfa-verify-button"
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full bg-primary text-primary-foreground rounded-md py-2.5 text-sm font-semibold hover:bg-primary/90 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Verify & sign in
              </button>
              <button
                type="button"
                data-testid="mfa-back-button"
                onClick={() => { setMfaStep(false); setCode(""); }}
                className="w-full text-sm text-muted-foreground hover:text-foreground"
              >
                Back to sign in
              </button>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                placeholder="you@clinic.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground rounded-md py-2.5 text-sm font-semibold hover:bg-primary/90 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign in
            </button>
          </form>
          )}

          <div className="mt-8 pt-6 border-t border-border">
            <p className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground mb-3">
              Demo accounts — click to fill
            </p>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  data-testid={`demo-${d.label.toLowerCase().replace(" ", "-")}`}
                  onClick={() => quick(d)}
                  className="text-left rounded-md border border-border bg-card px-3 py-2 hover:border-ring hover:shadow-sm transition-all"
                >
                  <span className="block text-sm font-medium">{d.label}</span>
                  <span className="block text-[11px] text-muted-foreground truncate">{d.email}</span>
                </button>
              ))}
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6" data-testid="staff-login-footer">
            Are you a patient?{" "}
            <Link to="/patient/login" className="font-medium text-foreground hover:underline" data-testid="goto-patient-login">
              Go to the patient portal
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
